class SfForeverClub extends HTMLElement {
  /** @type {{ klaviyoPublicKey: string, klaviyoListId: string }} */
  #config = { klaviyoPublicKey: '', klaviyoListId: '' };

  #instagramCount = 1;
  #tiktokCount = 1;

  connectedCallback() {
    try {
      const raw = this.querySelector('script[type="application/json"]');
      if (raw) this.#config = JSON.parse(raw.textContent);
    } catch {}

    this._onSubmit = this.#handleSubmit.bind(this);
    this._onAddInstagram = () => this.#addAccount('instagram');
    this._onAddTiktok = () => this.#addAccount('tiktok');

    this.querySelector('form')?.addEventListener('submit', this._onSubmit);
    this.querySelector('[data-add-instagram]')?.addEventListener('click', this._onAddInstagram);
    this.querySelector('[data-add-tiktok]')?.addEventListener('click', this._onAddTiktok);

    // Init remove-button visibility for first rows
    this.#updateRemoveButtons('[data-accounts="instagram"]');
    this.#updateRemoveButtons('[data-accounts="tiktok"]');
  }

  disconnectedCallback() {
    this.querySelector('form')?.removeEventListener('submit', this._onSubmit);
    this.querySelector('[data-add-instagram]')?.removeEventListener('click', this._onAddInstagram);
    this.querySelector('[data-add-tiktok]')?.removeEventListener('click', this._onAddTiktok);
  }

  /** @param {'instagram'|'tiktok'} type */
  #addAccount(type) {
    const container = this.querySelector(`[data-accounts="${type}"]`);
    if (!container) return;

    const count = type === 'instagram' ? ++this.#instagramCount : ++this.#tiktokCount;
    const label = type === 'instagram' ? 'Instagram' : 'TikTok';

    const row = document.createElement('div');
    row.className = 'sf-forever-club__account-row';
    row.innerHTML = `
      <input
        class="sf-forever-club__input"
        type="text"
        name="${type}[]"
        placeholder="@yourhandle"
        aria-label="${label} account ${count}"
      >
      <button type="button" class="sf-forever-club__remove-btn" aria-label="Remove account">
        <svg width="12" height="12" viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M1 1L11 11M11 1L1 11" stroke="currentColor" stroke-width="1.75" stroke-linecap="round"/>
        </svg>
      </button>
    `;

    row.querySelector('.sf-forever-club__remove-btn').addEventListener('click', () => {
      row.remove();
      this.#updateRemoveButtons(`[data-accounts="${type}"]`);
    });

    container.appendChild(row);
    this.#updateRemoveButtons(`[data-accounts="${type}"]`);
    row.querySelector('input')?.focus();
  }

  /** @param {string} containerSelector */
  #updateRemoveButtons(containerSelector) {
    const container = this.querySelector(containerSelector);
    if (!container) return;

    const rows = container.querySelectorAll('.sf-forever-club__account-row');
    const hasMultiple = rows.length > 1;

    rows.forEach((row) => {
      const btn = row.querySelector('.sf-forever-club__remove-btn');
      if (btn) btn.hidden = !hasMultiple;
    });
  }

  /** @param {SubmitEvent} event */
  async #handleSubmit(event) {
    event.preventDefault();

    if (!this.#validate()) return;

    const data = this.#collectData();
    this.#clearAlert();
    this.#setLoading(true);

    try {
      if (!this.#config.klaviyoPublicKey || !this.#config.klaviyoListId) {
        throw new Error('klaviyo_config_missing');
      }

      const response = await fetch(
        `https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(this.#config.klaviyoPublicKey)}`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'revision': '2024-02-15'
          },
          body: JSON.stringify({
            data: {
              type: 'subscription',
              attributes: {
                custom_source: 'Forever Club — Website',
                profile: {
                  data: {
                    type: 'profile',
                    attributes: {
                      email: data.email,
                      first_name: data.first_name,
                      last_name: data.last_name,
                      properties: {
                        'Forever Club - Instagram': data.instagram,
                        'Forever Club - TikTok': data.tiktok,
                        'Forever Club - Why Interested': data.why_interested,
                        'Forever Club - Date of Birth': data.date_of_birth,
                        'Forever Club - Country': data.country,
                        'Forever Club - Discord': data.discord,
                        'Forever Club - Age Verified': data.age_verified,
                        'Forever Club - Terms Agreed': data.terms_agreed
                      }
                    }
                  }
                }
              },
              relationships: {
                list: {
                  data: {
                    type: 'list',
                    id: this.#config.klaviyoListId
                  }
                }
              }
            }
          })
        }
      );

      // 202 = accepted; 409 = already subscribed to list (idempotent — also a success)
      if (response.status === 202 || response.status === 409) {
        this.#showSuccess();
      } else {
        const body = await response.json().catch(() => ({}));
        const detail = body?.errors?.[0]?.detail;
        this.#showAlert(detail || 'Something went wrong. Please try again.');
      }
    } catch (err) {
      const msg = err.message === 'klaviyo_config_missing'
        ? 'Form is not configured yet. Please contact the store administrator.'
        : 'Something went wrong. Please try again.';
      this.#showAlert(msg);
    } finally {
      this.#setLoading(false);
    }
  }

  #collectData() {
    const form = this.querySelector('form');

    const listValues = (name) =>
      [...form.querySelectorAll(`[name="${name}"]`)]
        .map((el) => el.value.trim())
        .filter(Boolean)
        .join(', ');

    return {
      first_name: form.querySelector('[name="first_name"]')?.value.trim() ?? '',
      last_name: form.querySelector('[name="last_name"]')?.value.trim() ?? '',
      email: form.querySelector('[name="email"]')?.value.trim() ?? '',
      instagram: listValues('instagram[]'),
      tiktok: listValues('tiktok[]'),
      why_interested: form.querySelector('[name="why_interested"]')?.value.trim() ?? '',
      date_of_birth: form.querySelector('[name="date_of_birth"]')?.value ?? '',
      country: form.querySelector('[name="country"]')?.value ?? '',
      discord: form.querySelector('[name="discord"]')?.value.trim() ?? '',
      age_verified: (/** @type {HTMLInputElement|null} */ (form?.querySelector('[name="age_verified"]')))?.checked ? 'Yes' : 'No',
      terms_agreed: (/** @type {HTMLInputElement|null} */ (form?.querySelector('[name="terms_agreed"]')))?.checked ? 'Yes' : 'No'
    };
  }

  #validate() {
    const form = this.querySelector('form');
    let valid = true;

    // Reset previous errors
    form.querySelectorAll('[aria-invalid]').forEach((el) => el.removeAttribute('aria-invalid'));

    // Required text/select/textarea fields
    const required = ['first_name', 'last_name', 'email', 'why_interested', 'date_of_birth', 'country'];
    for (const name of required) {
      const el = form.querySelector(`[name="${name}"]`);
      if (!el?.value.trim()) {
        el?.setAttribute('aria-invalid', 'true');
        valid = false;
      }
    }

    // Email format
    const emailEl = form.querySelector('[name="email"]');
    if (emailEl?.value && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailEl.value)) {
      emailEl.setAttribute('aria-invalid', 'true');
      valid = false;
    }

    // Instagram (first handle required)
    const firstInstagram = form.querySelector('[name="instagram[]"]');
    if (!firstInstagram?.value.trim()) {
      firstInstagram?.setAttribute('aria-invalid', 'true');
      valid = false;
    }

    // Required checkboxes
    form.querySelectorAll('[type="checkbox"][required]').forEach((cb) => {
      if (!cb.checked) {
        cb.setAttribute('aria-invalid', 'true');
        valid = false;
      }
    });

    if (!valid) {
      this.#showAlert('Please fill out all required fields correctly.');
      form.querySelector('[aria-invalid="true"]')?.focus();
    }

    return valid;
  }

  /** @param {boolean} loading */
  #setLoading(loading) {
    const btn = this.querySelector('[data-submit]');
    if (!btn) return;
    btn.disabled = loading;
    btn.classList.toggle('sf-forever-club__submit--loading', loading);
  }

  /** @param {string} message */
  #showAlert(message) {
    const el = this.querySelector('[data-alert]');
    if (!el) return;
    el.textContent = message;
    el.classList.add('sf-forever-club__alert--error');
    el.removeAttribute('hidden');
    el.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
  }

  #clearAlert() {
    const el = this.querySelector('[data-alert]');
    if (!el) return;
    el.textContent = '';
    el.setAttribute('hidden', '');
    el.classList.remove('sf-forever-club__alert--error');
  }

  #showSuccess() {
    const card = /** @type {HTMLElement|null} */ (this.querySelector('[data-form-card]'));
    const success = /** @type {HTMLElement|null} */ (this.querySelector('[data-success]'));
    if (!card || !success) return;

    card.hidden = true;
    success.hidden = false;
    success.scrollIntoView({ behavior: 'smooth', block: 'center' });

    setTimeout(() => {
      success.hidden = true;
      card.hidden = false;
      /** @type {HTMLFormElement|null} */ (this.querySelector('form'))?.reset();
      card.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 3000);
  }
}

if (!customElements.get('sf-forever-club')) {
  customElements.define('sf-forever-club', SfForeverClub);
}
